import { create } from "zustand";
import type { TileData, ViewData } from "@/lib/agent/types";
import { BREAKPOINTS } from "@/lib/constants";

/**
 * Whether an open panel sits BESIDE the chat (desktop) rather than over it.
 * Same breakpoint as `useIsDesktop`, which decides the inline-vs-overlay
 * layout in AgentPage — the two must agree or a "reveal" opens an overlay.
 */
export function panelFitsBesideChat(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`).matches;
}

export interface TileLayout {
  collapsed: boolean;
  height: number | null; // null = auto
}

export interface PanelState {
  // Panel
  isOpen: boolean;
  /** Something new landed in the panel while it was closed (mobile dot). */
  hasUnseen: boolean;
  panelWidth: number; // percentage

  // Tiles (per-session, set by usePanel hook)
  tiles: TileData[];

  // Per-tile layout (collapsed + height)
  tileLayouts: Record<string, TileLayout>;

  // Views (per-session)
  views: ViewData[];
  activeViewId: string | null;

  // File tracking — bumped when file tools run, triggers FilesView refresh
  fileVersion: number;
  fileCount: number;

  // Chat pre-fill (set by human task tile actions, consumed by AgentChat)
  pendingChatMessage: string | null;

  // Actions
  /** The user asked for the panel. Always opens. */
  openPanel: () => void;
  /**
   * The app WANTS the panel shown (entering a chat, a restored layout, a new
   * tile). On desktop that opens it beside the chat. On mobile the panel is
   * a full-screen overlay, so an unasked-for open hides the very chat the
   * user came to see — there it stays closed, and `markUnseen` lights a dot
   * on the toggle instead so the new content is still discoverable.
   */
  revealPanel: (opts?: { markUnseen?: boolean }) => void;
  closePanel: () => void;
  togglePanel: () => void;
  setPanelWidth: (width: number) => void;
  setTiles: (tiles: TileData[]) => void;
  addTile: (tile: TileData) => void;
  updateTile: (tileId: string, updates: Partial<TileData>) => void;
  removeTile: (tileId: string) => void;
  resetPanel: () => void;
  setPendingChatMessage: (message: string | null) => void;
  setTileLayouts: (layouts: Record<string, TileLayout>) => void;
  setTileLayout: (tileId: string, layout: Partial<TileLayout>) => void;
  setViews: (views: ViewData[]) => void;
  addView: (view: ViewData) => void;
  updateView: (viewId: string, updates: Partial<ViewData>) => void;
  removeView: (viewId: string) => void;
  setActiveViewId: (viewId: string | null) => void;
  reorderViews: (orderedViewIds: string[]) => void;
  bumpFileVersion: () => void;
  setFileCount: (count: number) => void;
}

const INITIAL_STATE = {
  isOpen: false,
  hasUnseen: false,
  panelWidth: 35,
  tiles: [] as TileData[],
  tileLayouts: {} as Record<string, TileLayout>,
  views: [] as ViewData[],
  activeViewId: null as string | null,
  fileVersion: 0,
  fileCount: 0,
  pendingChatMessage: null as string | null,
};

export const usePanelStore = create<PanelState>((set) => ({
  ...INITIAL_STATE,

  openPanel: () => set({ isOpen: true, hasUnseen: false }),
  revealPanel: (opts) =>
    set((s) => {
      if (s.isOpen) return {};
      if (panelFitsBesideChat()) return { isOpen: true, hasUnseen: false };
      return opts?.markUnseen ? { hasUnseen: true } : {};
    }),
  closePanel: () => set({ isOpen: false }),
  togglePanel: () => set((s) => (s.isOpen ? { isOpen: false } : { isOpen: true, hasUnseen: false })),
  setPanelWidth: (width) => set({ panelWidth: Math.max(20, Math.min(60, width)) }),

  setTiles: (tiles) => {
    console.log("[usePanelStore] setTiles called:", tiles.length, "types:", tiles.map((t) => t.type));
    set({ tiles });
  },

  addTile: (tile) => {
    set((s) => ({ tiles: [...s.tiles, tile] }));
    // Auto-open on first tile — beside the chat only; a dot on mobile.
    usePanelStore.getState().revealPanel({ markUnseen: true });
  },

  updateTile: (tileId, updates) =>
    set((s) => ({
      tiles: s.tiles.map((t) =>
        t.tileId === tileId ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t,
      ),
    })),

  removeTile: (tileId) =>
    set((s) => ({
      tiles: s.tiles.filter((t) => t.tileId !== tileId),
    })),

  resetPanel: () => set(INITIAL_STATE),
  setPendingChatMessage: (message) => set({ pendingChatMessage: message }),

  setTileLayouts: (layouts) => set({ tileLayouts: layouts }),
  setTileLayout: (tileId, layout) =>
    set((s) => ({
      tileLayouts: {
        ...s.tileLayouts,
        [tileId]: { ...{ collapsed: false, height: null }, ...s.tileLayouts[tileId], ...layout },
      },
    })),

  setViews: (views) => set({ views }),
  addView: (view) =>
    set((s) => ({
      views: [...s.views, view],
      activeViewId: s.activeViewId ?? view.viewId,
    })),
  updateView: (viewId, updates) =>
    set((s) => ({
      views: s.views.map((v) =>
        v.viewId === viewId ? { ...v, ...updates, updatedAt: new Date().toISOString() } : v,
      ),
    })),
  removeView: (viewId) =>
    set((s) => {
      const remaining = s.views.filter((v) => v.viewId !== viewId);
      return {
        views: remaining,
        activeViewId:
          s.activeViewId === viewId
            ? (remaining.find((v) => !v.isHidden)?.viewId ?? null)
            : s.activeViewId,
      };
    }),
  setActiveViewId: (viewId) => set({ activeViewId: viewId }),
  reorderViews: (orderedViewIds) =>
    set((s) => ({
      views: s.views.map((v) => {
        const idx = orderedViewIds.indexOf(v.viewId);
        return idx >= 0 ? { ...v, sortOrder: idx, updatedAt: new Date().toISOString() } : v;
      }),
    })),

  bumpFileVersion: () => set((s) => ({ fileVersion: s.fileVersion + 1 })),
  setFileCount: (count) => set({ fileCount: count }),
}));
